/**
 * Modular Filter System - Core Type Definitions
 *
 * This file defines the complete type system for the filter architecture.
 * All filters must conform to these types for type safety and consistency.
 */

/**
 * User tier levels for access control
 * Must match backend SUBSCRIPTION_TIERS in credits.ts
 */
export type UserTier = "STARTER" | "PRO" | "ELITE" | "UNLIMITED";

/**
 * Filter categories for logical grouping in the UI
 */
export type FilterCategory =
  | "financial"      // Yield, ROI, Cashflow, Price
  | "property"       // Bedrooms, Type, Tenure
  | "location"       // Crime, Transport, Area
  | "scoring"        // Total Score, Quality Rating
  | "ai"             // AI Verdict, Confidence
  | "advanced";      // Leasehold, Forecasts

/**
 * Supported filter types (extensible - add new types here)
 */
export type FilterType =
  | "range"          // Slider with min/max
  | "checkbox"       // Multiple selection
  | "select"         // Dropdown single selection
  | "toggle"         // Boolean on/off
  | "multiselect"    // Multiple selection dropdown
  | "search"         // Text search input
  | "location_search"; // Comma-separated location tokens (postcode/sector/outcode/town/county/region)

/**
 * Base filter interface - all filters extend this
 */
export interface BaseFilter {
  id: string;                    // Unique identifier (e.g., "gross_yield")
  name: string;                  // Display name (e.g., "Gross Yield")
  description: string;           // Tooltip/help text
  category: FilterCategory;      // Grouping category
  type: FilterType;              // Component type to render
  minTier: UserTier;             // Minimum tier required
  premiumFeature: boolean;       // Show premium badge?
  popularityRank: number;        // Sorting order (lower = higher priority)
  dataPath: string;              // JSON path to data (e.g., "btl_analysis.gross_yield")
  version: string;               // Version for tracking changes
  enabled: boolean;              // Admin can disable globally
}

/**
 * Type-specific filter configurations
 */
export interface FilterConfig {
  range: RangeFilterConfig;
  checkbox: CheckboxFilterConfig;
  select: SelectFilterConfig;
  toggle: ToggleFilterConfig;
  multiselect: MultiSelectFilterConfig;
  search: SearchFilterConfig;
  location_search: LocationSearchFilterConfig;
}

/**
 * Type-specific filter values
 */
export interface FilterValue {
  range: [number, number];
  checkbox: string[];
  select: string;
  toggle: boolean;
  multiselect: string[];
  search: string;
  location_search: string;
}

/**
 * Generic filter with type-specific config
 */
export interface Filter<T extends FilterType = FilterType> extends BaseFilter {
  type: T;
  config: FilterConfig[T];
  defaultValue: FilterValue[T];
  validator?: (value: FilterValue[T]) => boolean;
  transformer?: (value: FilterValue[T]) => any;  // Transform before filtering
}

/**
 * Range filter configuration
 */
export interface RangeFilterConfig {
  min: number;
  max: number;
  step: number;
  defaultMin: number;
  defaultMax: number;
  unit: string;
  format?: (value: number) => string;  // Custom formatting function
  presets?: Array<{
    label: string;
    value: [number, number];
    icon?: string;
  }>;
}

/**
 * Checkbox filter configuration
 */
export interface CheckboxFilterConfig {
  options: Array<{
    value: string;
    label: string;
    icon?: string;
    description?: string;
    disabled?: boolean;  // Disable individual options
    count?: number;  // Optional count to display next to option
  }>;
  defaultSelected: string[];
  multiSelect: boolean;
  searchable?: boolean;  // Show search box if many options
}

/**
 * Select filter configuration
 */
export interface SelectFilterConfig {
  options: Array<{
    value: string;
    label: string;
    icon?: string;
    count?: number;  // Optional count to display next to option
  }>;
  defaultValue: string;
  placeholder?: string;
  searchable?: boolean;
}

/**
 * Toggle filter configuration
 */
export interface ToggleFilterConfig {
  defaultValue: boolean;
  onLabel: string;
  offLabel: string;
}

/**
 * Multi-select filter configuration
 */
export interface MultiSelectFilterConfig {
  options: Array<{
    value: string;
    label: string;
    icon?: string;
    count?: number;  // Optional count to display next to option
  }>;
  defaultSelected: string[];
  maxSelections?: number;
  placeholder?: string;
}

/**
 * Search filter configuration
 */
export interface SearchFilterConfig {
  placeholder: string;
  debounceMs: number;
  minChars: number;
  helperText?: string;  // Optional helper text to display below the search input
}

/**
 * Location search filter configuration
 * Freeform comma-separated location tokens. Each token is classified server-side
 * by shape (full postcode / sector / outcode / town / county / region) and matched
 * with OR semantics across all populated buckets. See backend
 * propertySearchService.classifyLocationTokens for the routing rules.
 */
export interface LocationSearchFilterConfig {
  placeholder: string;
  debounceMs: number;
  helperText?: string;
}

/**
 * Filter permissions by tier
 */
export interface FilterPermissions {
  tier: UserTier;
  availableFilters: string[];      // Filter IDs user can access
  lockedFilters: string[];         // Filter IDs requiring upgrade
}

/**
 * Active filters state (what user has selected)
 */
export type ActiveFilters = Record<string, any>;

/**
 * Filter preset for quick filtering
 */
export interface FilterPreset {
  id: string;
  name: string;
  description: string;
  icon: string;
  filters: ActiveFilters;
}

/**
 * Saved filter preset (user-created)
 */
export interface SavedFilterPreset {
  id: string;
  name: string;
  description?: string;
  filters: ActiveFilters;
  createdAt: number;
  updatedAt: number;
}

/**
 * Category labels for display
 */
export const CATEGORY_LABELS: Record<FilterCategory, string> = {
  financial: "Financial Metrics",
  property: "Property Details",
  location: "Location Quality",
  scoring: "Investment Scores",
  ai: "AI Analysis",
  advanced: "Advanced Filters"
};

/**
 * Category icons for display
 */
export const CATEGORY_ICONS: Record<FilterCategory, string> = {
  financial: "",
  property: "",
  location: "",
  scoring: "",
  ai: "",
  advanced: ""
};

/**
 * Tier hierarchy for access control
 * Must match backend SUBSCRIPTION_TIERS in credits.ts
 */
export const TIER_HIERARCHY: Record<UserTier, number> = {
  STARTER: 0,
  PRO: 1,
  ELITE: 2,
  UNLIMITED: 3
};
